import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { parentApi, apiError } from "../../lib/api";
import { Card, Button, Input, Modal, Coins } from "../../components/ui";
import RewardVisual from "../../components/RewardVisual";
import AuthenticatedImage from "../../components/AuthenticatedImage";
import ConfirmDialog from "../../components/ConfirmDialog";
import TemplatePicker from "../../components/TemplatePicker";
import Icon from "../../components/Icon";
import { useAuth } from "../../context/AuthContext";
import { colorFor, REWARD_EMOJIS } from "../../lib/theme";

const ICONS = ["gift", "tablet", "film", "ice-cream-cone", "moon", "dollar-sign", "trees", "tent", "gamepad-2", "candy", "bike", "cake", "ticket", "star", "pizza", "cookie", "music", "palette", "rocket", "sparkles"];

export default function RewardsShop() {
  const { showEditControls, demoGuard } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [tab, setTab] = useState("templates");
  const [style, setStyle] = useState("emoji");
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", cost: 20, icon: "gift", emoji: "🎁", image_path: null });

  const load = useCallback(async () => {
    try {
      const [r, t, lib] = await Promise.all([
        parentApi.get("/rewards"), parentApi.get("/reward-templates"), parentApi.get("/rewards/photo-library"),
      ]);
      setRewards(r.data.rewards); setTemplates(t.data.templates); setLibrary(lib.data.photos || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);


  const openNew = () => { setEditing(null); setTab("templates"); setStyle("emoji"); setForm({ title: "", cost: 20, icon: "gift", emoji: "🎁", image_path: null }); setModal(true); };
  const openEdit = (r) => { setEditing(r); setStyle(r.image_path ? "photo" : r.emoji ? "emoji" : "icon"); setForm({ title: r.title, cost: r.cost, icon: r.icon || "gift", emoji: r.emoji || null, image_path: r.image_path || null }); setTab("custom"); setModal(true); };
  const pickTemplate = (t) => { setForm({ title: t.title, cost: t.cost, icon: t.icon, emoji: t.emoji, image_path: null }); setStyle("emoji"); setTab("custom"); };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (demoGuard()) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("photo", f); fd.append("kind", "reward");
      const { data } = await parentApi.post("/uploads/image", fd);
      setForm((s) => ({ ...s, image_path: data.path, emoji: null }));
      setLibrary((lib) => [{ path: data.path }, ...lib.filter((p) => p.path !== data.path)]);
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (demoGuard()) return;
    if (!form.title.trim()) { toast.error("Name your reward."); return; }
    const payload = { title: form.title, cost: form.cost, icon: form.icon,
      emoji: style === "emoji" ? form.emoji : null,
      image_path: style === "photo" ? form.image_path : null };
    try {
      if (editing) await parentApi.put(`/rewards/${editing.id}`, payload);
      else await parentApi.post("/rewards", payload);
      toast.success(editing ? "Reward updated" : "Reward added to the shop! 🎁");
      setModal(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { if (demoGuard()) return; await parentApi.delete(`/rewards/${id}`); toast.success("Reward removed"); load(); };

  if (loading) return <div className="grid place-items-center py-20"><div className="w-10 h-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="rewards-shop">
      <div className="flex items-start justify-between gap-3">
        <div><h1 className="font-heading font-black text-3xl text-ink">Treat Shop</h1><p className="text-ink-soft font-medium">Treats kids can buy with their coins.</p></div>
        <Button onClick={openNew} data-testid="new-reward-btn" className={showEditControls ? "" : "hidden"}><Icon name="plus" width={18} height={18} /> New</Button>
      </div>

      {rewards.length === 0 ? (
        <Card className="p-8 text-center"><Icon name="gift" className="mx-auto text-coral mb-3" width={36} height={36} /><p className="font-bold text-ink mb-4">Your shop is empty.</p>{showEditControls && <Button onClick={openNew}>Add a reward</Button>}</Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {rewards.map((r) => (
            <Card key={r.id} className="p-4 flex items-center gap-4 border-l-4" style={{ borderLeftColor: colorFor(r.id) }} data-testid="reward-row">
              <RewardVisual reward={r} color={colorFor(r.id)} size={56} context="parent" />
              <div className="flex-1 min-w-0"><div className="font-heading font-extrabold text-ink truncate">{r.title}</div><Coins amount={r.cost} className="text-sunny-fg text-lg" /></div>
              <div className="flex flex-col gap-1">
                {showEditControls && <button onClick={() => openEdit(r)} data-testid={`edit-reward-${r.id}`} className="w-9 h-9 rounded-full bg-slate-100 text-ink-soft grid place-items-center" aria-label="Edit"><Icon name="pencil" width={16} height={16} /></button>}
                {showEditControls && <button onClick={() => { if (demoGuard()) return; setConfirmDel(r); }} data-testid={`delete-reward-${r.id}`} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-coral/20 hover:text-coral text-ink-soft grid place-items-center" aria-label="Delete"><Icon name="trash-2" width={16} height={16} /></button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? "Edit reward" : "New reward"} testid="reward-modal">
        {!editing && (
          <div className="flex gap-2 mb-4 bg-slate-100 rounded-2xl p-1">
            {[["templates", "Ideas"], ["custom", "Custom"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`flex-1 py-2 rounded-xl text-sm font-bold ${tab === k ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`} data-testid={`reward-tab-${k}`}>{l}</button>
            ))}
          </div>
        )}

        {tab === "templates" && !editing ? (
          <div>
            <TemplatePicker
              templates={templates} kind="reward" accent="coral"
              renderItem={(t) => (
                <button onClick={() => pickTemplate(t)} data-testid="reward-template-item" className="w-full flex items-center gap-2 p-3 rounded-2xl border-2 border-slate-100 hover:border-coral/40 text-left transition-colors">
                  <span className="text-2xl">{t.emoji}</span>
                  <div className="min-w-0"><div className="text-sm font-bold text-ink truncate">{t.title}</div><Coins amount={t.cost} className="text-xs text-sunny-fg" /></div>
                </button>
              )}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center"><RewardVisual reward={{ ...form, emoji: style === "emoji" ? form.emoji : null, image_path: style === "photo" ? form.image_path : null }} color={colorFor(form.title || "x")} size={72} context="parent" /></div>
            <Input label="Reward name" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Ice cream trip" data-testid="reward-title" />
            <div>
              <span className="block mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft">Cost (coins)</span>
              <input type="number" min="0" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseInt(e.target.value || "0", 10) })} className="w-full rounded-2xl border-2 border-slate-200 px-4 py-3 font-bold text-ink focus-ring" data-testid="reward-cost" />
            </div>
            <div className="flex gap-2 bg-slate-100 rounded-2xl p-1">
              {[["emoji", "Emoji"], ["icon", "Icon"], ["photo", "Photo"]].map(([k, l]) => (
                <button key={k} onClick={() => setStyle(k)} className={`flex-1 py-2 rounded-xl text-sm font-bold ${style === k ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`} data-testid={`style-${k}`}>{l}</button>
              ))}
            </div>
            {style === "emoji" && (
              <div className="grid grid-cols-8 gap-1.5 max-h-40 overflow-y-auto no-scrollbar">
                {REWARD_EMOJIS.map((em) => (<button key={em} onClick={() => setForm({ ...form, emoji: em, image_path: null })} className={`aspect-square rounded-xl grid place-items-center text-xl ${form.emoji === em ? "bg-coral/15 ring-2 ring-coral" : "bg-slate-50"}`} data-testid={`emoji-${em}`}>{em}</button>))}
              </div>
            )}
            {style === "icon" && (
              <div className="grid grid-cols-7 gap-2 max-h-40 overflow-y-auto no-scrollbar">
                {ICONS.map((ic) => (<button key={ic} onClick={() => setForm({ ...form, icon: ic })} className={`aspect-square rounded-xl grid place-items-center ${form.icon === ic ? "bg-coral text-white" : "bg-slate-100 text-ink-soft"}`} data-testid={`reward-icon-${ic}`}><Icon name={ic} width={20} height={20} /></button>))}
              </div>
            )}
            {style === "photo" && (
              <div className="space-y-3">
                {library.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2 text-xs font-bold uppercase tracking-[0.15em] text-ink-soft"><Icon name="images" width={14} height={14} /> Photo library</div>
                    <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto no-scrollbar" data-testid="reward-photo-library">
                      {library.map((p) => (
                        <button key={p.path} type="button" onClick={() => setForm((s) => ({ ...s, image_path: p.path, emoji: null }))}
                          data-testid="library-photo"
                          className={`aspect-square rounded-2xl overflow-hidden border-2 transition-all ${form.image_path === p.path ? "border-coral ring-2 ring-coral scale-95" : "border-slate-200 hover:border-coral/40"}`}>
                          <AuthenticatedImage path={p.path} context="parent" alt="Saved reward" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                    <div className="text-[11px] font-bold text-ink-muted text-center my-1">— or upload a new one —</div>
                  </div>
                )}
                <label className="flex flex-col items-center justify-center gap-2 py-6 rounded-2xl border-2 border-dashed border-coral/30 bg-coral/5 cursor-pointer" data-testid="reward-photo-label">
                  {form.image_path ? <span className="font-bold text-primary flex items-center gap-1"><Icon name="check" width={16} height={16} /> Photo selected — tap to upload another</span>
                    : <><Icon name="camera" className="text-coral" width={28} height={28} /><span className="font-bold text-ink">{uploading ? "Uploading…" : "Upload a photo"}</span></>}
                  <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="reward-photo-input" />
                </label>
              </div>
            )}
            <Button onClick={save} size="lg" className="w-full" disabled={uploading} data-testid="save-reward">{editing ? "Save changes" : "Add to shop"}</Button>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)}
        onConfirm={async () => { await remove(confirmDel.id); setConfirmDel(null); }}
        title="Remove this reward?" message={confirmDel ? `“${confirmDel.title}” will disappear from the shop.` : ""}
        confirmLabel="Remove reward" testid="delete-reward-confirm" />
    </div>
  );
}
